import axios from "axios";
import { Invite } from "../foundation/Modals/InviteUserModal/InviteUserModal";
import { GroupPermissions } from "../mobile/SelectContactsScreenGroups";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Instância do axios com configuração padrão
const api = axios.create({
  //baseURL: "https://8d5679b815e3.ngrok-free.app",
//  headers: {
//    "ngrok-skip-browser-warning": "true",
//  },
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos de timeout
});

// Interceptor de resposta para melhor tratamento de erros
api.interceptors.response.use(
  (response) => {
    console.log(`[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    console.error(`[API] Erro em ${error.config?.method?.toUpperCase()} ${error.config?.url}:`, {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });
    return Promise.reject(error);
  }
);

const token = localStorage.getItem("@backofficev2:token"); // Recupere o token de onde você o armazena

// Função genérica para tratar requisições com token
const handleRequest = async (request: () => Promise<any>, token?: string) => {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const response = await request();
    return response.data;
  } catch (error: any) {
    console.error("Erro na requisição:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        data: error.config?.data,
      },
    });

    // Log da mensagem de erro específica do backend
    if (error.response?.data?.error) {
      console.error("Erro do backend:", error.response.data.error);
    }

    // Re-throw o erro original para manter a estrutura de erro do axios
    throw error;
  }
};

// Requisições ajustadas para incluir o token
export const fetchMyGroups = async (userId: string, token: string) => {
  return handleRequest(() =>
    api.get(`/group/list-groups/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};
export const createNewGroup = async (
  name: string,
  description: string,
  ownerUsername: string,
  token: string,
  groupKey: string,
  permissions?: GroupPermissions,
  memberUsernames?: string[]
) => {
  // Payload padrão com permissões padrão se não fornecidas
  const defaultPermissions: GroupPermissions = {
    membersCanEditSettings: true,
    membersCanSendMessages: true,
    membersCanAddMembers: true,
    adminsMustAuthorizeNewMembers: false,
  };

  const payload = {
    name,
    description,
    ownerUsername,
    groupKey,
    memberUsernames: memberUsernames || [],
    ...(permissions || defaultPermissions),
  };

  console.log("[API] Payload enviado para criação de grupo:", payload);

  return handleRequest(() =>
    api.post("/group/", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  );
};

export const deleteGroupById = async (id: string, token: string, ownerUsername: string) => {
  return handleRequest(() =>
    api.delete(`/group/delete`, {
      data: { id, ownerUsername },
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

export const updateMemberRole = async (
  groupId: string,
  memberId: string,
  newRole: "ADMIN" | "MEMBER",
  token: string,
  promovedBy: string | undefined
) => {
  return handleRequest(() =>
    api.patch(
      "/group/members/update-role",
      { groupId, memberId, newRole, promovedBy },
      { headers: { Authorization: `Bearer ${token}` } }
    )
  );
};

export const listInvitedGroupsInGraphicAccount = async (graphicAccountId: string, token: string): Promise<Invite[]> => {
  try {
    const response = await api.get(`/group/list-groups-invites/${graphicAccountId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || [];
  } catch (error) {
    console.error("Erro ao buscar convites de grupos:", error);
    return [];
  }
};

export const updateGroupStatus = async (groupId: string, status: string, token: string) => {
  return handleRequest(() =>
    api.post(`/group/update-status`, { groupId, status }, { headers: { Authorization: `Bearer ${token}` } })
  );
};

export const blockUserInGroup = async (groupId: string, username: string, token: string) => {
  return handleRequest(() =>
    api.post(`/group/block-user`, { groupId, username }, { headers: { Authorization: `Bearer ${token}` } })
  );
};

export const sendInviteToGroup = async (groupId: string, username: string, token: string) => {
  return handleRequest(() =>
    api.post(`/group/invite`, { groupId, username }, { headers: { Authorization: `Bearer ${token}` } })
  );
};

export const editGroup = async (
  groupName: string, // Nome atual do grupo
  newGroupName?: string, // Novo nome do grupo (opcional)
  newGroupDescription?: string, // Nova descrição do grupo (opcional)
  token?: string, // Token de autenticação
  editedBy?: string
) => {
  // Criação dinâmica do payload com base nos parâmetros fornecidos
  const data: { groupName: string; newName?: string; newDescription?: string; editedBy?: string } = { groupName };

  if (newGroupName) data.newName = newGroupName; // Adiciona `newName` ao payload apenas se for fornecido
  if (newGroupDescription) data.newDescription = newGroupDescription; // Adiciona `newDescription` ao payload apenas se for fornecido
  if (editedBy) data.editedBy = editedBy;

  // Envia a requisição para o backend
  return handleRequest(() =>
    api.patch(`/group/edit`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  );
};
export const editGroupById = async (
  groupId: string, // Nome atual do grupo
  newGroupName?: string, // Novo nome do grupo (opcional)
  newGroupDescription?: string, // Nova descrição do grupo (opcional)
  token?: string,
  editedBy?: string
) => {
  // Criação dinâmica do payload com base nos parâmetros fornecidos
  const data: { groupId: string; newName?: string; newDescription?: string; editedBy?: string } = { groupId };

  if (newGroupName) data.newName = newGroupName; // Adiciona `newName` ao payload apenas se for fornecido
  if (newGroupDescription) data.newDescription = newGroupDescription; // Adiciona `newDescription` ao payload apenas se for fornecido
  if (editedBy) data.editedBy = editedBy;

  console.log("Iniciando requisição de edição de grupo...");
  console.log("Payload:", data);

  // Envia a requisição para o backend
  return handleRequest(() =>
    api.patch(`/group/edit-by-id`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  );
};

// Outra função para pegar grupos, com base na resposta que você deu
export const fetchAllGroups = async (userName: string, token: string) => {
  return handleRequest(() =>
    api.get(`/group/list/${userName}/all-groups`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

// Função para buscar permissões de um grupo específico
export const fetchGroupPermissions = async (groupId: string, token: string) => {
  return handleRequest(() =>
    api.get(`/group/${groupId}/permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

// Função para atualizar permissões de um grupo
export const updateGroupPermissions = async (groupId: string, permissions: GroupPermissions, token: string) => {
  return handleRequest(() =>
    api.patch(`/group/${groupId}/permissions`, permissions, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

// Função para buscar membros de um grupo específico
export const fetchGroupMembers = async (groupId: string, token: string) => {
  return handleRequest(() =>
    api.get(`/group/members/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

// Função para buscar dados de um usuário por ID
export const fetchUserById = async (userId: string, token: string) => {
  return handleRequest(() =>
    api.get(`/graphic/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

// Função alternativa para buscar informações do grupo (pode incluir membros)
export const fetchGroupInfo = async (groupId: string, token: string) => {
  return handleRequest(() =>
    api.get(`/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

export const addMembersToGroup = async (
  groupId: string,
  usernames: string[],
  token: string,
  addedBy: string | undefined
) => {
  return handleRequest(() =>
    api.post(
      "/group/members/add-multiple", // Rota correta para múltiplos usuários
      { groupId, usernames, addedBy },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
  );
};

export const removeMemberFromGroup = async (
  groupId: string,
  username: string,
  token: string,
  removerBy: string | undefined
) => {
  console.log("groupId", groupId, username, token, removerBy);

  return handleRequest(() =>
    api.post(
      "/group/remove-user", // Rota correta
      { groupId, username, removerBy },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
  );
};

export const leaveGroup = async (groupId: string, token: string, userName: string) => {
  return handleRequest(() =>
    api.post(
      "/group/members/leave", // Rota correta
      { groupId, userName }, // O backend pegará o username do token
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
  );
};
